/*
 * OS-level notifications for rows that arrive while the app is open.
 *
 * Deliberately NOT Web Push: there is no service worker and no subscription: this only
 * ever fires for a tab that is already running and already holding the socket. That is
 * the whole feature — telling someone who has the app open but is looking at something
 * else. A tab that is closed has no socket, so there is nothing to announce.
 *
 * Everything here degrades to false rather than throwing. The caller falls back to the
 * in-app toast, which is why no branch is allowed to be fatal: a browser that refuses
 * the constructor must cost the user a toast, not the notification.
 */

/** The app's own icon, from public/. 192px is the size notification UIs want. */
const ICON = '/logo192.png';

/*
 * Ids already announced in this tab.
 *
 * Two hook instances can be mounted at once — the Notification Center screen renders
 * the Navbar, whose bell mounts useNotifications, and then mounts it again itself — so
 * a single socket event reaches two handlers. Without this the user gets two popups.
 * Module scope is what makes it shared: a ref inside the hook would be per-instance,
 * which is exactly the thing being deduplicated.
 */
const announced = new Set();
const ANNOUNCED_LIMIT = 500;

/**
 * True the first time an id is seen, false every time after.
 *
 * The set is trimmed rather than left to grow: a long-lived tab on a busy workspace
 * would otherwise hold every id it had ever seen. Oldest-first, since a repeat of a
 * very old id is not a case worth spending memory on.
 */
export const firstSighting = (id) => {
  const key = String(id);
  if (announced.has(key)) return false;

  announced.add(key);
  if (announced.size > ANNOUNCED_LIMIT) {
    announced.delete(announced.values().next().value);
  }

  return true;
};

export const isSupported = () => typeof window !== 'undefined'
  && typeof window.Notification === 'function';

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export const getPermission = () => (isSupported() ? window.Notification.permission : 'unsupported');

/**
 * Ask for permission. Must be called from a user gesture — browsers ignore (and
 * Safari rejects) a request that is not, and a refused prompt cannot be re-shown.
 */
export const requestPermission = async () => {
  if (!isSupported()) return 'unsupported';

  try {
    return await window.Notification.requestPermission();
  } catch (error) {
    return getPermission();
  }
};

/*
 * Whether the user is actually looking at this tab. `visibilityState` alone is not
 * enough: a visible tab in an unfocused window still reads 'visible', and that user is
 * looking at something else — which is the case worth interrupting.
 */
export const isAppInForeground = () => {
  if (typeof document === 'undefined') return false;
  const visible = document.visibilityState === 'visible';

  return visible && (typeof document.hasFocus === 'function' ? document.hasFocus() : true);
};

/** The row's title is an array of parts, where an object part is emphasised text. */
export const plainTitle = (title) => {
  if (typeof title === 'string') return title;
  if (!Array.isArray(title)) return '';

  return title
    .map((part) => (part && typeof part === 'object' ? part.b || '' : String(part || '')))
    .join('')
    .trim();
};

/** Title plus whatever context the row already carries, as one line. */
export const notificationText = (item) => ({
  title: plainTitle(item.title) || 'New notification',
  body: [item.dateRange, item.policy, item.duration, item.after]
    .filter(Boolean)
    .join(' · ') || item.note || '',
});

/**
 * Show one. Returns false when nothing was shown, so the caller can fall back.
 *
 * `tag` is the row id, which makes several open tabs collapse to a single popup: the
 * browser replaces a notification carrying a tag it already has rather than stacking
 * another. In-tab duplicates are firstSighting's job; this covers across tabs, where
 * no shared state exists.
 */
export const showDesktopNotification = (item, onClick) => {
  if (!isSupported() || getPermission() !== 'granted') return false;

  const { title, body } = notificationText(item);

  try {
    const notification = new window.Notification(title, {
      body,
      icon: ICON,
      tag: `shoot-notification-${item.id}`,
      // Replacing a same-tag notification should update it quietly, not re-alert.
      renotify: false,
    });

    notification.onclick = () => {
      // The point of the popup: bring them back to the tab that raised it.
      window.focus();
      notification.close();
      if (onClick) onClick(item);
    };

    return true;
  } catch (error) {
    // Chrome on Android throws "Illegal constructor" — it requires a service worker
    // registration for notifications. Nothing to do but let the toast handle it.
    return false;
  }
};
